/**
 * A cena da imagem de divulgação.
 *
 * Aqui está o "segundo desenho" do produto: a agenda **aberta**, com a espiral à
 * mostra, sobre um fundo escolhido. É CSS puro — as páginas são as mesmas do
 * papel (`PaginaImpressa`), só que em perspectiva; quem vira arquivo é o Chromium
 * do servidor, que fotografa esta cena. Nada disto passa perto do PDF.
 *
 * Três decisões que valem registrar:
 *
 * - a cena é desenhada num **canvas de pixels fixos** (1080 × 1080 ou 1080 × 1920),
 *   e a agenda é ajustada por conta, não por olhômetro: a mesma cena sai igual no
 *   arquivo e na prévia do assistente;
 * - a perspectiva é 3D de verdade (`perspective` + `rotateY`), porque é ela que faz
 *   a agenda parecer aberta na mão em vez de dois retângulos colados;
 * - a sombra é um **elemento** atrás da agenda, e não um `filter` no container: um
 *   filtro achataria o 3D e as páginas perderiam o ângulo em silêncio.
 */

import {
  MEDIDAS_MOCKUP,
  OCUPACAO_MOCKUP,
  medirPagina,
  type ConfigPlanner,
  type DocumentoMiolo,
  type FundoMockup,
  type OpcoesMockup,
  type Pagina,
  type Paleta,
} from '@miolos/core';
import { PaginaImpressa } from '@/impressao/PaginaImpressa';
import { urlDaImagem } from '@/lib/api';

const PX_POR_MM = 96 / 25.4;

/** O que cada fundo desenha atrás da agenda. */
export function fundoDoMockup(fundo: FundoMockup, paleta: Paleta): { css: string; escuro: boolean } {
  switch (fundo) {
    case 'MESA':
      return {
        escuro: true,
        css: [
          'repeating-linear-gradient(96deg, rgba(0,0,0,0.05) 0 2px, rgba(255,255,255,0.03) 2px 7px)',
          'repeating-linear-gradient(90deg, rgba(0,0,0,0.04) 0 26px, rgba(255,255,255,0.02) 26px 52px)',
          'radial-gradient(120% 80% at 50% 12%, rgba(255,255,255,0.22), transparent 60%)',
          'linear-gradient(178deg, #cba077, #a97a4e 46%, #7f5733)',
        ].join(', '),
      };
    case 'LISA':
      return {
        escuro: false,
        css: `radial-gradient(120% 90% at 50% 8%, ${paleta.principalClaro}, ${paleta.principal})`,
      };
    case 'DEGRADE':
      return {
        escuro: true,
        css: `linear-gradient(155deg, ${paleta.principalEscuro} 0%, ${paleta.principal} 45%, ${paleta.destaque} 100%)`,
      };
    case 'PAPEL':
      return {
        escuro: false,
        css: [
          'repeating-linear-gradient(45deg, rgba(0,0,0,0.022) 0 1px, transparent 1px 5px)',
          'repeating-linear-gradient(-45deg, rgba(0,0,0,0.018) 0 1px, transparent 1px 5px)',
          'radial-gradient(120% 90% at 50% 10%, #ffffff, #f1ede5 70%, #e3ddd2)',
        ].join(', '),
      };
    case 'ESCURA':
    default:
      return {
        escuro: true,
        css: [
          'radial-gradient(70% 45% at 50% 42%, rgba(255,255,255,0.16), transparent 70%)',
          'linear-gradient(180deg, #2b2f36, #191c21 70%)',
        ].join(', '),
      };
  }
}

/** Quais páginas a cena mostra: a ordem é esquerda, direita. */
export function paginasDaCena(documento: DocumentoMiolo, cena: OpcoesMockup['cena']): Pagina[] {
  const dias = documento.paginas.filter((pagina) => pagina.tipo === 'DIA');
  const capa = documento.paginas.find((pagina) => pagina.tipo === 'CAPA');

  if (cena === 'DOIS_DIAS') {
    /* Miolo sem dois dias (agenda curta, só cadernos): repete o que existe. */
    const primeira = dias[0] ?? documento.paginas[0];
    const segunda = dias[1] ?? primeira;
    return [primeira, segunda].filter((pagina): pagina is Pagina => Boolean(pagina));
  }

  const esquerda = capa ?? documento.paginas[0];
  const direita = dias[0] ?? documento.paginas[1] ?? esquerda;
  return [esquerda, direita].filter((pagina): pagina is Pagina => Boolean(pagina));
}

interface Props {
  config: ConfigPlanner;
  paleta: Paleta;
  paginas: Pagina[];
  opcoes: OpcoesMockup;
  /**
   * Escala de exibição. 1 = tamanho do arquivo, que é o que o servidor fotografa.
   * A prévia do assistente usa menos de 1 para a imagem caber no diálogo.
   */
  fator?: number;
  className?: string;
}

export function CenaMockup({ config, paleta, paginas, opcoes, fator = 1, className }: Props) {
  const medida = MEDIDAS_MOCKUP[opcoes.formato];
  const fundo = fundoDoMockup(opcoes.fundo, paleta);

  const primeira = paginas[0];
  const segunda = paginas[1] ?? primeira;

  /* Sem página não há cena: quem monta a página já avisa o servidor antes disso. */
  if (!primeira || !segunda) return null;

  const medidas = medirPagina(primeira.formato, {
    orientacao: config.papel?.orientacao,
    sangriaMm: config.sangriaMm ?? 0,
  });
  const paginaPx = { largura: medidas.larguraMm * PX_POR_MM, altura: medidas.alturaMm * PX_POR_MM };

  /*
   * A conta do tamanho: a agenda aberta cabe na largura pedida e, no que sobra de
   * altura, o pé ainda recebe a assinatura. Vale o menor dos dois.
   */
  const escala = Math.min(
    (medida.larguraPx * OCUPACAO_MOCKUP[opcoes.formato]) / (paginaPx.largura * 2),
    (medida.alturaPx * (opcoes.formato === 'STORIES' ? 0.62 : 0.66)) / paginaPx.altura,
  );

  const larguraFolha = paginaPx.largura * escala;
  const alturaFolha = paginaPx.altura * escala;

  /* No stories a agenda fica acima do centro, porque a assinatura vive embaixo. */
  const topoBase = opcoes.formato === 'STORIES' ? '42%' : '48%';

  const inclinacao = opcoes.formato === 'STORIES' ? 15 : 13;
  const corDaAssinatura = fundo.escuro ? 'rgba(255,255,255,0.72)' : 'rgba(31,41,55,0.62)';

  const argolas = Math.max(9, Math.round(alturaFolha / 42));
  const larguraArgola = Math.max(11, alturaFolha * 0.028);

  const papel = (
    pagina: Pagina,
    lado: 'ESQUERDO' | 'DIREITO',
    rotacao: number,
    origem: string,
  ) => (
    <div
      style={{
        width: larguraFolha,
        height: alturaFolha,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        transform: `rotateY(${rotacao}deg)`,
        transformOrigin: origem,
        boxShadow: fundo.escuro
          ? '0 0 0 1px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.4)'
          : '0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.18)',
        background: '#fff',
      }}
    >
      <div
        style={{
          width: `${medidas.larguraMm}mm`,
          height: `${medidas.alturaMm}mm`,
          transform: `scale(${escala})`,
          transformOrigin: 'top left',
        }}
      >
        <PaginaImpressa
          pagina={pagina}
          paleta={paleta}
          config={config}
          lado={lado}
          resolverImagem={urlDaImagem}
        />
      </div>
    </div>
  );

  return (
    <div
      className={className}
      style={{
        width: medida.larguraPx,
        height: medida.alturaPx,
        transform: `scale(${fator})`,
        transformOrigin: 'top left',
        position: 'relative',
        overflow: 'hidden',
        background: fundo.css,
      }}
    >
      {/* Sombra da agenda: elemento próprio, senão o filtro achataria o 3D. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: topoBase,
          width: larguraFolha * 2.1,
          height: alturaFolha * 0.34,
          transform: 'translate(-50%, -34%)',
          background: 'radial-gradient(50% 50% at 50% 50%, rgba(0,0,0,0.45), transparent 72%)',
          filter: 'blur(18px)',
          opacity: fundo.escuro ? 0.75 : 0.4,
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: topoBase,
          transform: 'translate(-50%, -50%)',
          perspective: 2600,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            transformStyle: 'preserve-3d',
            transform: 'rotateX(5deg)',
          }}
        >
          {papel(primeira, 'DIREITO', inclinacao, 'right center')}
          {papel(segunda, 'ESQUERDO', -inclinacao, 'left center')}
        </div>

        {/* Espiral: no vão entre as duas páginas, e por cima delas. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: 0,
            height: alturaFolha,
            transform: 'translateX(-50%)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: -5,
              top: 0,
              width: 10,
              height: alturaFolha,
              background: 'linear-gradient(90deg, rgba(0,0,0,0.32), rgba(0,0,0,0.06))',
            }}
          />
          {Array.from({ length: argolas }, (_, indice) => (
            <span
              key={indice}
              style={{
                position: 'absolute',
                top: ((indice + 0.5) * alturaFolha) / argolas,
                left: -larguraArgola / 2,
                width: larguraArgola,
                height: larguraArgola * 0.62,
                transform: 'translateY(-50%)',
                borderRadius: 999,
                background: 'linear-gradient(180deg, #f7f9fb, #a9b1ba 55%, #6f767e)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.35), inset 0 -1px 1px rgba(0,0,0,0.25)',
              }}
            />
          ))}
        </div>
      </div>

      {opcoes.assinatura ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: medida.alturaPx * 0.06,
            transform: 'translateX(-50%)',
            maxWidth: medida.larguraPx * 0.9,
            fontSize: Math.round(medida.larguraPx * 0.024),
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            textAlign: 'center',
            lineHeight: 1.5,
            color: corDaAssinatura,
            fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {opcoes.assinatura}
        </div>
      ) : null}
    </div>
  );
}
